import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { allFaqs } from "@/mocks/faqs";
import { siteInfo } from "@/mocks/site";

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "常见问题", item: `${SITE_URL}/faq` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="常见问题 — 瓜乐圈内容更新与使用说明"
        description="瓜乐圈常见问题解答：内容栏目有哪些、多久更新一次、适合什么时候看、如何投稿推荐，以及收藏与要点速览的使用说明。"
        keywords="瓜乐圈常见问题,福利吃瓜,内容更新,投稿推荐"
        path="/faq"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
            <p className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-question-answer-line"></i>
              帮助中心
            </p>
            <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-5xl">
              常见问题
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              关于 {siteInfo.name} 的内容方向、更新节奏和使用方式，我们把读者问得最多的问题整理在这里。
              如果还是没找到答案，欢迎直接来信。
            </p>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-6">
                <div className="rounded-xl2 border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                    <i className="ri-mail-send-line text-xl"></i>
                  </span>
                  <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">还有其他问题？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    写信告诉我们你遇到的情况，通常会在两个工作日内收到回复。
                  </p>
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="mt-4 inline-flex items-center gap-2 break-all text-sm text-primary-600 hover:text-primary-700"
                  >
                    <i className="ri-mail-line"></i>
                    {siteInfo.email}
                  </a>
                </div>

                <div className="rounded-xl2 border border-accent-200 bg-accent-50 p-6">
                  <h2 className="font-heading text-base font-bold text-accent-950">快速跳转</h2>
                  <ul className="mt-3 space-y-3 text-sm text-accent-900">
                    <li>
                      <Link to="/category/mingchangmian" className="flex items-center gap-2 hover:text-accent-700">
                        <i className="ri-star-smile-line"></i>
                        名场面栏目
                      </Link>
                    </li>
                    <li>
                      <Link to="/category/baoxiao" className="flex items-center gap-2 hover:text-accent-700">
                        <i className="ri-emotion-laugh-line"></i>
                        爆笑高能栏目
                      </Link>
                    </li>
                    <li>
                      <Link to="/category/xiaotiangua" className="flex items-center gap-2 hover:text-accent-700">
                        <i className="ri-heart-3-line"></i>
                        小甜瓜栏目
                      </Link>
                    </li>
                    <li>
                      <Link to="/category/lezi" className="flex items-center gap-2 hover:text-accent-700">
                        <i className="ri-gift-2-line"></i>
                        乐子合集栏目
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8">
              <div className="divide-y divide-background-200 overflow-hidden rounded-xl2 border border-background-200 bg-background-50">
                {allFaqs.map((faq, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <div key={faq.question}>
                      <h2>
                        <button
                          type="button"
                          onClick={() => setOpenIndex(isOpen ? null : index)}
                          className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-5 text-left md:px-6"
                          aria-expanded={isOpen}
                        >
                          <span className="font-heading text-sm font-bold text-foreground-950 md:text-base">
                            {faq.question}
                          </span>
                          <i
                            className={`shrink-0 text-lg text-primary-500 transition-transform ${
                              isOpen ? "ri-subtract-line" : "ri-add-line"
                            }`}
                          ></i>
                        </button>
                      </h2>
                      {isOpen && (
                        <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600 md:px-6">{faq.answer}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col gap-4 rounded-xl2 border border-secondary-200 bg-secondary-50 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-heading text-base font-bold text-secondary-950">准备好继续吃瓜了吗</h2>
                  <p className="mt-1 text-sm text-secondary-900/80">从四个栏目里挑一个，随时开始。</p>
                </div>
                <Link
                  to="/"
                  className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
                >
                  回到首页
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}